/* WENNEBIRD R3: submit the exact selected variant; never auto-retry an uncertain POST. */
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        if (this.initializeForm()) return;
        this._wbInitObserver?.disconnect();
        this._wbInitObserver = new MutationObserver(() => {
          if (this.initializeForm()) this._wbInitObserver.disconnect();
        });
        this._wbInitObserver.observe(this, { childList: true, subtree: true });
      }

      disconnectedCallback() {
        this._wbInitObserver?.disconnect();
      }

      initializeForm() {
        if (this._wbFormInitialized) return true;
        const form = this.querySelector('form');
        const input = form?.querySelector('[name=id]');
        const button = this.querySelector('[type="submit"]');
        if (!form || !input || !button) return false;
        this.form = form;
        this.submitButton = button;
        this.submitButtonText = button.querySelector('span') || button;
        input.disabled = false;
        this._wbSubmitHandler = this.onSubmitHandler.bind(this);
        this.form.addEventListener('submit', this._wbSubmitHandler);
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
        this.hideErrors = this.dataset.hideErrors === 'true';
        this._wbFormInitialized = true;
        return true;
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const productRoot = this.closest('product-info');
        if (productRoot?.dataset.wbFitmentInvalid === 'true' || productRoot?.dataset.wbQuantityBlocked === 'true') {
          this.handleErrorMessage('Review the selected vehicle configuration and quantity before adding to cart.');
          return;
        }
        if (!this.form.reportValidity()) return;
        this.error = false;
        this.handleErrorMessage();
        const variantIdInput = this.variantIdInput;
        if (!variantIdInput || !/^\d+$/.test(variantIdInput.value) || !Number.isSafeInteger(Number(variantIdInput.value)) || Number(variantIdInput.value) <= 0) {
          this.handleErrorMessage('Please select a valid vehicle and mat set before adding to cart.');
          return;
        }

        const selectedText = this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]')?.textContent;
        if (selectedText) {
          try {
            const selected = JSON.parse(selectedText);
            if (!selected || String(selected.id) !== variantIdInput.value) {
              this.handleErrorMessage('The vehicle selection is still updating. Select your configuration again.');
              return;
            }
          } catch { this.handleErrorMessage('The vehicle selection could not be confirmed. Please select it again.'); return; }
        }
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        const spinner = this.querySelector('.loading__spinner');
        spinner?.classList.remove('hidden');

        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 15000);
        let added = false;
        try {
          const config = fetchConfig('json');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          const formData = new FormData(this.form);
          if (this.cart) {
            formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
          config.signal = controller.signal;

          const response = await fetch(routes.cart_add_url, config);
          const contentType = response.headers.get('content-type') || '';
          if (!contentType.includes('json')) throw new Error('Unexpected cart response type');
          const payload = await response.json();
          if (!response.ok || payload.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: payload.errors || payload.description,
              message: payload.message,
            });
            this.handleErrorMessage(payload.description || payload.message || (window.cartStrings?.error || 'The item could not be added. Please review your selection.'));
            return;
          }
          added = true;
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: payload,
          });
          if (!this.cart) {
            window.location.assign(window.routes.cart_url);
            return;
          }
          const displayCart = () => {
            try {
              this.cart.renderContents(payload);
            } catch (error) {
              console.error('Unable to refresh cart drawer', error);
              window.location.assign(window.routes.cart_url);
            }
          };
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => window.setTimeout(displayCart), { once: true });
            quickAddModal.hide(true);
          } else {
            displayCart();
          }
        } catch (error) {
          console.error('Add to Cart could not be confirmed', error);
          this.handleErrorMessage(
            added
              ? 'Added to cart, but the cart preview could not load. Open your cart to review it.'
              : 'We could not confirm the add-to-cart request. Check your cart before trying again.'
          );
        } finally {
          window.clearTimeout(timeout);
          this.submitButton.classList.remove('loading');
          if (this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          this.submitButton.removeAttribute('aria-disabled');
          spinner?.classList.add('hidden');
        }
      }

      handleErrorMessage(errorMessage = false) {
        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const productRoot = this.closest('product-info');
        const guarded = productRoot?.dataset.wbFitmentInvalid === 'true' || productRoot?.dataset.wbQuantityBlocked === 'true';
        if (disable || guarded) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = (window.variantStrings?.addToCart || 'Add to Cart');
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
